import { Typography, Tooltip } from 'antd/es';
import { ReactElement, useMemo, useState } from 'react';
import { format } from 'date-fns';
import i18n from 'i18next';
import { Link } from 'react-router-dom';
import { Button, Spin, Upload, UploadFile } from 'antd';
import {Trans, useTranslation} from 'react-i18next';
import { RcFile } from 'antd/es/upload';
import classNames from 'classnames';

import { SettingsCard } from './SettingsCard';
import { SettingsDisabledInput } from './SettingsDisabledInput';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import Utils from 'utils/utils';
import { Paths, TaxFormStatus, ClientRole } from 'utils/constants';
import { language } from 'utils';

import { Object, DoubleTaxationItem, UserSettings } from 'models';

import { uploadTaxDocuments } from 'features/userSettings/actions';

import { ReactComponent as Close } from 'assets/icons/close.svg';
import { ReactComponent as AttachIcon } from 'assets/icons/attach.svg';

import { selectCountries, selectUser, selectUserSettings } from 'selectors';

import s from './index.module.scss';
import upload from './upload.module.scss';
import cn from 'classnames';
import classnames from "classnames";

const MAX_COUNT_FILES = 4;
const MAX_SIZE = 10485760;

const WORDS = [
	'taxResidencyText',
	'countryResidencyText',
	'yourTaxRateText',
	'dasFormText',
	'readMoreResidencyText',
	'taxResidencyNotSpecifiedText',
	'specifyNowText',
	'errorBigFileText',
	'attachText',
	'sendDocumentsText',
	'statusDocumentsText',
	'errorMaxFilesText',
	'pIdSettingsText',
];

const TAX_FORM_STATUSES: Object = {
	PENDING: i18n.t('underConsiderationText'),
	APPROVED: i18n.t('approvedText'),
	REJECTED: i18n.t('rejectedText'),
	EXPIRED: i18n.t('expiredText'),
};

const LT = 'LT';

export const SettingsResidencyCard = () => {
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const t = useTranslate(WORDS);
	const tr = useTranslation();

	const dispatch = useAppDispatch();

	const countries = useAppSelector(selectCountries);
	const userSettings = useAppSelector(selectUserSettings);
	const user = useAppSelector(selectUser);

	const [isLoading, setIsLoading] = useState(false);

	const [error, setError] = useState('');

	const country = useMemo(
		() =>
			countries?.find((item: any) => item.iso === userSettings.tax_residence?.country?.iso)
				?.title || '',
		[countries, userSettings],
	);

	const beforeUpload = (file: RcFile, files: RcFile[]) => {
		if (error) {
			setError('');
		}
		if (fileList.length + files.length > MAX_COUNT_FILES) {
			setError(t.errorMaxFilesText);
			return;
		}

		const uploadedFiles = files
			.map((item) => {
				if (item.size > MAX_SIZE) {
					setError(t.errorBigFileText);
				}

				return item;
			})
			.filter((item) => item?.size < MAX_SIZE);

		setFileList([...fileList, ...uploadedFiles]);

		return false;
	};
	const onUploadFiles = () => {
		const formData = new FormData();

		fileList.forEach((item, i) => {
			return formData.append(`files[${i}]`, item as RcFile);
		});

		if (fileList) {
			setIsLoading(true);
			dispatch(uploadTaxDocuments(formData))
				.unwrap()
				.catch((errors: any) => {
					setError(i18n.t(errors.errors?.files ? errors.errors.files[0] : errors.message));
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	};

	const docStatus = userSettings.tax_residence?.double_taxation
		? userSettings.tax_residence.double_taxation[0].status
		: '';

	const isShowDasForm =
		userSettings.tax_residence?.country.iso !== LT &&
		user.userType === ClientRole.USER &&
		docStatus !== TaxFormStatus.APPROVED &&
		docStatus !== TaxFormStatus.PENDING;

	const renderRateText = (userSettings: UserSettings) => {
		return (
			<div className={s.rateText}>
				{t.yourTaxRateText}
				<Typography.Text type="danger">
					&nbsp;
					{userSettings.tax_residence?.tax?.rate &&
					Number.isInteger(+userSettings.tax_residence.tax?.rate)
						? Math.trunc(+userSettings.tax_residence.tax?.rate)
						: userSettings.tax_residence?.tax?.rate}
					%
				</Typography.Text>
			</div>
		);
	};

	const isEn = language === 'en';

	return (
		<SettingsCard title={t.taxResidencyText}>
			{userSettings.tax_residence ? (
				<>
					<div>
						<div className={s.rateRow}>
							<SettingsDisabledInput
								className={classnames(s.col, s.inputMaxWith312)}
								name={t.countryResidencyText}
								value={country}
							/>

							<div className={s.rateWrapperDesktop}>{renderRateText(userSettings)}</div>
						</div>
						<div className={s.rateRow}>
							<SettingsDisabledInput
								className={classnames(s.col, s.inputMaxWith312)}
								name={t.pIdSettingsText}
								value={userSettings.tax_residence.identification_number}
							/>
						</div>

						<div className={s.rateRow}>
							<div className={s.rateWrapperMobile}>{renderRateText(userSettings)}</div>
						</div>
					</div>
					{isShowDasForm && (
						<div className={s.uploadRow}>
							<div className={upload.root}>
								<Typography.Text className={upload.text}>{t.dasFormText}</Typography.Text>

								<label htmlFor="file" className={upload.label}>
									<Upload
										accept="image/jpeg,image/jpg,image/png,application/pdf"
										className={upload.upload}
										multiple
										name="file"
										listType="text"
										maxCount={MAX_COUNT_FILES}
										showUploadList={{
											showRemoveIcon: true,
											showDownloadIcon: false,
											showPreviewIcon: false,
										}}
										onRemove={(file) => {
											const index = fileList.indexOf(file);
											const newFileList = fileList.slice();
											newFileList.splice(index, 1);
											setFileList(newFileList);
										}}
										beforeUpload={beforeUpload}
										fileList={fileList}
										itemRender={(
											originNode: ReactElement,
											file: UploadFile,
											fileList: object[],
											actions: { remove: any },
										) => {
											return (
												<div key={file.name} className={`filter ${upload.filter}`}>
													<span className={upload.filterText}>{file.name}</span>

													<Button type="link" icon={<Close />} onClick={actions.remove} />
												</div>
											);
										}}
									>
										<Tooltip
											placement="topRight"
											overlayClassName="popover"
											title={
												<Trans
													i18nKey="filePDFText"
													components={{
														list_item: <li />,
														list: (
															<ul className={upload.list} onClick={(e) => e.stopPropagation()} />
														),
														das_link: (
															<a
																className={upload.link}
																href={Utils.getLinkToWP(
																	'/wp-content/uploads/2023/12/DAS-1-FR0021.pdf',
																	true,
																)}
																target="_blank"
																rel="noreferrer"
															/>
														),
													}}
												/>
											}
										>
											<Button className={upload.button}>
												{isLoading ? (
													<span className={upload.spin}>
														<Spin />
													</span>
												) : (
													<AttachIcon className={upload.attach} />
												)}
												{t.attachText}&nbsp;
											</Button>
										</Tooltip>
									</Upload>

									{error && (
										<Typography.Text
											className={classNames(upload.error, {
												[upload.errorEmpty]: fileList.length === 0,
											})}
											type="danger"
										>
											{tr.t(error)}
										</Typography.Text>
									)}
								</label>
							</div>

							{fileList.length > 0 && (
								<Button
									type="primary"
									className={s.sendDas}
									onClick={onUploadFiles}
									loading={isLoading}
								>
									{t.sendDocumentsText}
								</Button>
							)}
						</div>
					)}
					{userSettings.tax_residence.double_taxation && (
						<div className={s.doubleTaxation}>
							<Typography.Text className={s.statusText}>{t.statusDocumentsText}:</Typography.Text>

							{userSettings.tax_residence.double_taxation.map((item, i) => {
								const status = item.status;

								const content = getContent(item, item.reject_reason);

								const isShowDates =
									status === TaxFormStatus.EXPIRED || status === TaxFormStatus.APPROVED;

								let validUntil = new Date(),
									validSince = new Date();

								if (isShowDates) {
									validSince = new Date(item.valid_since?.replace(/-/g, '/'));
									validUntil = new Date(item.valid_until?.replace(/-/g, '/'));
								}

								return (
									<div className={s.doubleTaxationRow} key={i}>
										<div>{t.dasFormText}</div>

										<Tooltip
											overlayClassName={cn('popover', s.popover)}
											title={
												status === TaxFormStatus.REJECTED ? (
													<span dangerouslySetInnerHTML={{ __html: item.reject_reason }} />
												) : (
													<Trans
														i18nKey={content}
														values={{
															since: format(validSince, 'yyyy-MM-dd'),
															until: format(validUntil, 'yyyy-MM-dd'),
														}}
													/>
												)
											}
											placement={'topRight'}
										>
											<div
												className={classNames(s.attachedStatus, {
													[s.red]: status === TaxFormStatus.REJECTED,
													[s.green]: status === TaxFormStatus.APPROVED,
													[s.blue]: status === TaxFormStatus.EXPIRED,
												})}
											>
												{TAX_FORM_STATUSES[status]}
											</div>
										</Tooltip>
									</div>
								);
							})}
						</div>
					)}{' '}
				</>
			) : (
				<div className={s.doubleTaxationRow}>
					<Typography.Text>{t.taxResidencyNotSpecifiedText}</Typography.Text>
					<Button className={s.specifyNow}>
						<Link to={Paths.RESIDENCY} state={{ fromOverview: true }}>
							{t.specifyNowText}
						</Link>
					</Button>
				</div>
			)}

			<Typography.Text type="secondary" className={s.readMore}>
				<a
					className={s.link}
					href={`https://crowdpear.com/${
						isEn ? 'help-category/taxes/' : 'lt/duk-category/mokesciai/'
					}`}
					target="_blank"
					rel="noreferrer"
				>
					{t.readMoreResidencyText}
				</a>
			</Typography.Text>
		</SettingsCard>
	);
};

const getContent = (item: DoubleTaxationItem, reason: string) => {
	let content = 'validPeriodText';

	if (item.status === TaxFormStatus.PENDING) {
		content = 'pendingDasStatusText';
	}

	if (item.status === TaxFormStatus.REJECTED) {
		content = reason;
	}

	return content;
};
